import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import {
  LoginView,
  LoginWithTokenView,
  ResetPasswordView,
  UnauthorizedView,
  VerifyOtpView,
} from "@/features/auth";
import { RouteFallback } from "@/shared/ui";
import { AppShell } from "@/widgets/app-shell";

/**
 * SPA route table (react-router v6). Public routes render standalone;
 * protected routes render inside <AppShell/>, which gates the session
 * (redirects to /login when unauthenticated) and provides the sidebar/
 * topbar chrome with an <Outlet/> for page content. Pages are thin — each
 * route renders a feature view from its @/features/<x> public barrel.
 *
 * Every protected view is lazy-loaded (React.lazy + dynamic import of the
 * feature barrel) so each feature ships as its own chunk instead of one
 * monolithic bundle. Auth views and the shell stay eager for a fast first
 * paint on /login.
 */
const DashboardView = lazy(() =>
  import("@/features/dashboard").then((m) => ({ default: m.DashboardView }))
);
const ClinicsView = lazy(() =>
  import("@/features/clinics").then((m) => ({ default: m.ClinicsView }))
);
// Admin claims list — distinct from the member-portal ClaimsView below.
const AdminClaimsView = lazy(() =>
  import("@/features/claims").then((m) => ({ default: m.ClaimsView }))
);
const PartnersView = lazy(() =>
  import("@/features/partners").then((m) => ({ default: m.PartnersView }))
);
const EmployersView = lazy(() =>
  import("@/features/employers").then((m) => ({ default: m.EmployersView }))
);
const GroupsView = lazy(() => import("@/features/groups").then((m) => ({ default: m.GroupsView })));
const DivisionsView = lazy(() =>
  import("@/features/divisions").then((m) => ({ default: m.DivisionsView }))
);
const LocationsView = lazy(() =>
  import("@/features/locations").then((m) => ({ default: m.LocationsView }))
);
const MembersView = lazy(() =>
  import("@/features/members").then((m) => ({ default: m.MembersView }))
);
const HsaMembersView = lazy(() =>
  import("@/features/hsa-members").then((m) => ({ default: m.HsaMembersView }))
);
const UsersView = lazy(() => import("@/features/users").then((m) => ({ default: m.UsersView })));
const CustomersListView = lazy(() =>
  import("@/features/customers").then((m) => ({ default: m.CustomersListView }))
);
const CustomerProfileView = lazy(() =>
  import("@/features/customers").then((m) => ({ default: m.CustomerProfileView }))
);
const BillingPreferencesListView = lazy(() =>
  import("@/features/billing-preferences").then((m) => ({ default: m.BillingPreferencesListView }))
);
const BillingPreferenceDetailView = lazy(() =>
  import("@/features/billing-preferences").then((m) => ({ default: m.BillingPreferenceDetailView }))
);
const BillingPreferenceFormView = lazy(() =>
  import("@/features/billing-preferences").then((m) => ({ default: m.BillingPreferenceFormView }))
);
const BillingAccountsListView = lazy(() =>
  import("@/features/billing-accounts").then((m) => ({ default: m.BillingAccountsListView }))
);
const BillingAccountDetailView = lazy(() =>
  import("@/features/billing-accounts").then((m) => ({ default: m.BillingAccountDetailView }))
);
const PaymentCheckoutView = lazy(() =>
  import("@/features/payments").then((m) => ({ default: m.PaymentCheckoutView }))
);
const StatementPayView = lazy(() =>
  import("@/features/statements").then((m) => ({ default: m.StatementPayView }))
);
const PaymentsView = lazy(() =>
  import("@/features/payments").then((m) => ({ default: m.PaymentsView }))
);
const AddInvoiceView = lazy(() =>
  import("@/features/invoices").then((m) => ({ default: m.AddInvoiceView }))
);
const ReconciliationView = lazy(() =>
  import("@/features/reconciliation").then((m) => ({ default: m.ReconciliationView }))
);
const ReportsView = lazy(() =>
  import("@/features/reports").then((m) => ({ default: m.ReportsView }))
);

/* Commission / distribution (dev catch-up features) */
const CommissionDashboardView = lazy(() =>
  import("@/features/commission-dashboard").then((m) => ({ default: m.CommissionDashboardView }))
);
const CommissionRulesView = lazy(() =>
  import("@/features/commission-rules").then((m) => ({ default: m.CommissionRulesView }))
);
const AgencyListView = lazy(() =>
  import("@/features/agency-configuration").then((m) => ({ default: m.AgencyListView }))
);
const AgencyDetailView = lazy(() =>
  import("@/features/agency-configuration").then((m) => ({ default: m.AgencyDetailView }))
);
const AgentConfigurationView = lazy(() =>
  import("@/features/agent-configuration").then((m) => ({ default: m.AgentConfigurationView }))
);
const AgentSetupWizard = lazy(() =>
  import("@/features/agent-configuration").then((m) => ({ default: m.AgentSetupWizard }))
);
const AgentDetailRedirect = lazy(() =>
  import("@/features/agent-configuration").then((m) => ({ default: m.AgentDetailRedirect }))
);
const AgentPortalView = lazy(() =>
  import("@/features/agent-portal").then((m) => ({ default: m.AgentPortalView }))
);
const BrokersView = lazy(() =>
  import("@/features/brokers").then((m) => ({ default: m.BrokersView }))
);
const BrokerDetailRoute = lazy(() =>
  import("@/features/brokers").then((m) => ({ default: m.BrokerDetailRoute }))
);
const DistributionView = lazy(() =>
  import("@/widgets/distribution-page").then((m) => ({ default: m.DistributionPage }))
);
const PayoutLedgerView = lazy(() =>
  import("@/features/payout-ledger").then((m) => ({ default: m.PayoutLedgerView }))
);
const OutOfCycleView = lazy(() =>
  import("@/features/out-of-cycle").then((m) => ({ default: m.OutOfCycleView }))
);

/* Member portal (shares one chunk — all views come from the same barrel) */
const MemberPortalView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.MemberPortalView }))
);
const MemberDashboardLayout = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.MemberDashboardLayout }))
);
const InsuranceOverviewView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.InsuranceOverviewView }))
);
const ClaimsView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.ClaimsView }))
);
const CoverageView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.CoverageView }))
);
const DeductiblesView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.DeductiblesView }))
);
const FamilyView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.FamilyView }))
);
const FindDoctorView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.FindDoctorView }))
);
const MemberCardView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.MemberCardView }))
);
const ProfileView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.ProfileView }))
);
const TeleCallView = lazy(() =>
  import("@/features/member-portal").then((m) => ({ default: m.TeleCallView }))
);

// Param-reading wrappers for detail/edit routes whose views take `id` as a prop.
function BillingPreferenceDetailRoute() {
  const { id = "" } = useParams();
  return <BillingPreferenceDetailView id={id} />;
}
function BillingPreferenceEditRoute() {
  const { id = "" } = useParams();
  return <BillingPreferenceFormView mode="edit" id={id} />;
}
function BillingAccountDetailRoute() {
  const { id = "" } = useParams();
  return <BillingAccountDetailView id={Number(id)} />;
}

/**
 * The /payment door. It is the per-invoice checkout, but a STATEMENT link can arrive here too:
 * the mailed "View and pay" button was built on the account's stored pay-page address, which still
 * names this legacy page (2026-10-01: 17 bills mailed that way). The checkout reads a transaction
 * id, finds none, and would print "Link expired" for a valid bill — so a link carrying a
 * statementId is handed to the statement pay page with its whole query (tv, ltk) intact.
 */
function PaymentEntry() {
  const { search } = useLocation();
  if (new URLSearchParams(search).has("statementId")) {
    return <Navigate to={`/payment/statement${search}`} replace />;
  }
  return <PaymentCheckoutView />;
}

export function App() {
  return (
    // Catches the public routes, which have no shell worth keeping on screen.
    // Everything inside AppShell suspends against its own boundary so the rail
    // and header survive a chunk fetch.
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginView />} />
        <Route path="/login-with-token" element={<LoginWithTokenView />} />
        <Route path="/verify-otp" element={<VerifyOtpView />} />
        <Route path="/unauthorized" element={<UnauthorizedView />} />
        <Route path="/payment" element={<PaymentEntry />} />
        <Route path="/payment/statement" element={<StatementPayView />} />

        {/* Authenticated but deliberately outside the shell: the shell sends a
            user with a forced password change here, so it cannot loop. */}
        <Route path="/reset-password" element={<ResetPasswordView />} />

        {/* Protected — inside the app shell (AppShell gates the session) */}
        <Route element={<AppShell />}>
          <Route path="/dashboard" element={<DashboardView />} />
          <Route path="/clinics" element={<ClinicsView />} />
          <Route path="/claims" element={<AdminClaimsView />} />
          <Route path="/partners" element={<PartnersView />} />
          <Route path="/employers" element={<EmployersView />} />
          <Route path="/groups" element={<GroupsView />} />
          <Route path="/divisions" element={<DivisionsView />} />
          <Route path="/locations" element={<LocationsView />} />
          <Route path="/members" element={<MembersView />} />
          <Route path="/hsa-members" element={<HsaMembersView />} />
          <Route path="/users" element={<UsersView />} />
          <Route path="/customers" element={<CustomersListView />} />
          <Route path="/customers/:id" element={<CustomerProfileView />} />
          <Route path="/billing-preferences" element={<BillingPreferencesListView />} />
          <Route
            path="/billing-preferences/new"
            element={<BillingPreferenceFormView mode="create" />}
          />
          <Route path="/billing-preferences/:id" element={<BillingPreferenceDetailRoute />} />
          <Route path="/billing-preferences/:id/edit" element={<BillingPreferenceEditRoute />} />
          <Route path="/billing-accounts" element={<BillingAccountsListView />} />
          <Route path="/billing-accounts/:id" element={<BillingAccountDetailRoute />} />
          <Route path="/payments" element={<PaymentsView />} />
          <Route path="/invoices/add" element={<AddInvoiceView />} />
          {/* Alias for the account bill view reached from an entity grid's "Billing account" /
              "Current Invoice" — same screen as /billing-accounts/:id, but under /invoices so the
              sidebar highlight stays on the grid the user came from (mirrors dev). */}
          <Route path="/invoices/billing-account/:id" element={<BillingAccountDetailRoute />} />
          <Route path="/reconcillation" element={<ReconciliationView />} />
          <Route path="/reports" element={<ReportsView />} />
          <Route path="/member-portal" element={<MemberPortalView />} />

          {/* Commission / distribution (dev catch-up features) */}
          <Route path="/commission-dashboard" element={<CommissionDashboardView />} />
          <Route path="/commission-rules" element={<CommissionRulesView />} />
          <Route path="/agency-configuration" element={<AgencyListView />} />
          <Route path="/agency-configuration/:id" element={<AgencyDetailView />} />
          <Route path="/agent-configuration" element={<AgentConfigurationView />} />
          <Route path="/agent-configuration/new" element={<AgentSetupWizard />} />
          <Route path="/agent-configuration/:brokerId" element={<AgentDetailRedirect />} />
          <Route path="/agent-portal" element={<AgentPortalView />} />
          <Route path="/agent-portal/:brokerId" element={<AgentPortalView />} />
          <Route path="/brokers" element={<BrokersView />} />
          <Route path="/brokers/:brokerId" element={<BrokerDetailRoute />} />
          <Route path="/distribution" element={<DistributionView />} />
          <Route path="/payout-ledger" element={<PayoutLedgerView />} />
          <Route path="/out-of-cycle" element={<OutOfCycleView />} />

          {/* Member portal — nested under its own shell */}
          <Route path="/dashboard/:hsaUniqueId" element={<MemberDashboardLayout />}>
            <Route index element={<InsuranceOverviewView />} />
            <Route path="claims" element={<ClaimsView />} />
            <Route path="coverage" element={<CoverageView />} />
            <Route path="deductibles" element={<DeductiblesView />} />
            <Route path="family" element={<FamilyView />} />
            <Route path="find-doctor" element={<FindDoctorView />} />
            <Route path="member-card" element={<MemberCardView />} />
            <Route path="profile" element={<ProfileView />} />
            <Route path="tele-call" element={<TeleCallView />} />
          </Route>
        </Route>

        {/* Root + fallback */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}
